import { Alert, Button, Center, Loader, SimpleGrid, Stack, Text, Title } from '@mantine/core'
import { IconAlertCircle, IconPlayerPlay } from '@tabler/icons-react'
import { soundboardQuery } from '@features/soundboard/queries'
import { getAuthenticatedPortalApiUrl } from '@services/api/portalApi'
import { requireRouteAudience } from '@services/router/routeAccess'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useRef, useState } from 'react'

export const Route = createFileRoute('/_layout/tools/soundboard')({
    component: SoundboardPage,
    beforeLoad: requireRouteAudience(['staff']),
    staticData: { crumb: { label: 'Soundboard' } },
})

const colors = [
    'blue',
    'teal',
    'green',
    'lime',
    'yellow',
    'orange',
    'red',
    'pink',
    'grape',
    'violet',
]

function SoundboardPage() {
    const { data: sounds, isPending, error } = useQuery(soundboardQuery)
    const audioRef = useRef<HTMLAudioElement>(null)
    const [playing, setPlaying] = useState<string | null>(null)
    const [playError, setPlayError] = useState<string | null>(null)

    const play = async (fileName: string) => {
        try {
            setPlayError(null)
            const audio = audioRef.current
            if (!audio) return
            audio.src = await getAuthenticatedPortalApiUrl(`soundboard/${encodeURIComponent(fileName)}`)
            await audio.play()
            setPlaying(fileName)
        } catch {
            setPlaying(null)
            setPlayError('The sound could not be played.')
        }
    }

    return (
        <Stack p="md">
            {isPending && <Center><Loader aria-label="Loading sounds" /></Center>}
            {error && <Alert icon={<IconAlertCircle />} color="red">Unable to load sounds.</Alert>}
            {playError && <Alert icon={<IconAlertCircle />} color="red">{playError}</Alert>}
            {sounds?.length === 0 && <Text c="dimmed">No MP3 files are available.</Text>}
            <SimpleGrid cols={{ base: 1, sm: 2, md: 5 }}>
                {sounds?.map((fileName, index) => (
                    <Button
                        key={fileName}
                        leftSection={<IconPlayerPlay size={18} />}
                        variant={playing === fileName ? 'filled' : 'light'}
                        onClick={() => void play(fileName)}
                        h={150}
                        color={colors[index % colors.length]}
                    >
                        {fileName.replace(/\.mp3$/i, '')}
                    </Button>
                ))}
            </SimpleGrid>
            <audio ref={audioRef} onEnded={() => setPlaying(null)} onError={() => setPlaying(null)} />
        </Stack>
    )
}
